import React, { useState, useEffect } from 'react';
import { generateAgentKeys, isEd25519Supported } from '../utils/crypto';
import KeyGenerationModal from '../components/KeyGenerationModal';

const AgentTable = ({ agents, onRegisterMore, onOpenModal, openTooltip, onTooltipToggle, currentPage, setCurrentPage, agentsPerPage }) => {
  // State management for copy functionality
  const [copiedStates, setCopiedStates] = useState({});
  const [hoveredButtons, setHoveredButtons] = useState({});

  // Calculate pagination
  const totalAgents = agents.length;
  const totalPages = Math.ceil(totalAgents / agentsPerPage);
  const startIndex = (currentPage - 1) * agentsPerPage;
  const endIndex = startIndex + agentsPerPage;
  const currentAgents = agents.slice(startIndex, endIndex);

  // Pagination handlers
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Copy handlers
  const handleCopyPublicKey = (agentId, publicKey) => {
    if (!publicKey) return;

    navigator.clipboard.writeText(publicKey).then(() => {
      setCopiedStates(prev => ({ ...prev, [`${agentId}_publicKey`]: true }));
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [`${agentId}_publicKey`]: false }));
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy public key:', err);
    });
  };

  const handleCopyRegistryUrl = (agentId, registryUrl) => {
    navigator.clipboard.writeText(registryUrl).then(() => {
      setCopiedStates(prev => ({ ...prev, [`${agentId}_registryUrl`]: true }));
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [`${agentId}_registryUrl`]: false }));
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy registry URL:', err);
    });
  };

  // Function to shorten registry URL for display
  const shortenRegistryUrl = (url, maxLength = 45) => {
    if (url.length <= maxLength) return url;
    
    // Extract the important parts: domain and agent ID
    const urlParts = url.split('/');
    const agentId = urlParts[urlParts.length - 1]; // AG_xxxxx
    
    // For table display, show a very compact version that's still meaningful
    return `nekuda-registry.../${agentId}`;
  };

  const handleCopyAgentId = (agentId) => {
    navigator.clipboard.writeText(agentId).then(() => {
      setCopiedStates(prev => ({ ...prev, [`${agentId}_agentId`]: true }));
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [`${agentId}_agentId`]: false }));
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy agent ID:', err);
    });
  };

  return (
    <div className="agent-table-container">
      <h2 className="form-title" style={{ margin: '0 0 10px 0' }}>Registered Agents</h2>
      <p style={{ 
        margin: '0 0 20px 0', 
        fontSize: '16px', 
        color: 'rgba(0,0,0,0.6)', 
        fontFamily: "'Rubik', sans-serif",
        textAlign: 'center'
      }}>
        Total agents: <strong>{totalAgents}</strong>
      </p>
      
      {/* Desktop Table View */}
      <table className="agent-table">
        <thead>
          <tr>
            <th>Agent ID</th>
            <th>Public Key</th>
            <th>Registry URL</th>
          </tr>
        </thead>
        <tbody>
          {currentAgents
            .filter(agent => agent && agent.agentId) // Filter out null/undefined agents
            .map(agent => (
            <tr key={agent.agentId}>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <span>{agent.agentId}</span>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <button
                      onClick={() => handleCopyAgentId(agent.agentId)}
                      onMouseEnter={() => setHoveredButtons(prev => ({ ...prev, [`${agent.agentId}_agentId`]: true }))}
                      onMouseLeave={() => setHoveredButtons(prev => ({ ...prev, [`${agent.agentId}_agentId`]: false }))}
                      style={{
                        background: copiedStates[`${agent.agentId}_agentId`] ? '#28a745'
                                   : hoveredButtons[`${agent.agentId}_agentId`] ? '#007bff' : 'none',
                        border: `1px solid ${copiedStates[`${agent.agentId}_agentId`] ? '#28a745'
                                   : hoveredButtons[`${agent.agentId}_agentId`] ? '#007bff' : '#ccc'}`,
                        borderRadius: '4px',
                        padding: '4px 8px',
                        cursor: 'pointer',
                        fontSize: '10px',
                        color: copiedStates[`${agent.agentId}_agentId`] || hoveredButtons[`${agent.agentId}_agentId`] ? 'white' : '#666',
                        minWidth: '40px',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s ease-in-out',
                        position: 'relative'
                      }}
                      className="copy-button"
                    >
                      {copiedStates[`${agent.agentId}_agentId`] ? '✓' : 'Copy'}
                    </button>

                    {(hoveredButtons[`${agent.agentId}_agentId`] || copiedStates[`${agent.agentId}_agentId`]) && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: '100%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          backgroundColor: '#333',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          whiteSpace: 'nowrap',
                          zIndex: '1000',
                          marginBottom: '4px',
                          pointerEvents: 'none',
                          opacity: 1,
                          visibility: 'visible',
                          animation: 'fadeIn 0.2s ease-in-out'
                        }}
                        className="copy-tooltip"
                      >
                        {copiedStates[`${agent.agentId}_agentId`] ? 'Copied!' : 'Copy Agent ID'}
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: '0',
                          height: '0',
                          borderLeft: '4px solid transparent',
                          borderRight: '4px solid transparent',
                          borderTop: '4px solid #333'
                        }}></div>
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ 
                    fontFamily: 'monospace', 
                    fontSize: '11px', 
                    color: 'rgba(0,0,0,0.7)',
                    maxWidth: '120px',
                    display: 'inline-block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {agent.technical?.jwk?.x || 'Not provided'}
                  </span>
                  {agent.technical?.jwk?.x && (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <button
                        onClick={() => handleCopyPublicKey(agent.agentId, agent.technical?.jwk?.x)}
                        onMouseEnter={() => setHoveredButtons(prev => ({ ...prev, [`${agent.agentId}_publicKey`]: true }))}
                        onMouseLeave={() => setHoveredButtons(prev => ({ ...prev, [`${agent.agentId}_publicKey`]: false }))}
                        style={{
                          background: copiedStates[`${agent.agentId}_publicKey`] ? '#28a745'
                                     : hoveredButtons[`${agent.agentId}_publicKey`] ? '#007bff' : 'none',
                          border: `1px solid ${copiedStates[`${agent.agentId}_publicKey`] ? '#28a745'
                                     : hoveredButtons[`${agent.agentId}_publicKey`] ? '#007bff' : '#ccc'}`,
                          borderRadius: '4px',
                          padding: '4px 8px',
                          cursor: 'pointer',
                          fontSize: '10px',
                          color: copiedStates[`${agent.agentId}_publicKey`] || hoveredButtons[`${agent.agentId}_publicKey`] ? 'white' : '#666',
                          minWidth: '40px',
                          whiteSpace: 'nowrap',
                          transition: 'all 0.2s ease-in-out',
                          position: 'relative'
                        }}
                        className="copy-button"
                      >
                        {copiedStates[`${agent.agentId}_publicKey`] ? '✓' : 'Copy'}
                      </button>

                      {(hoveredButtons[`${agent.agentId}_publicKey`] || copiedStates[`${agent.agentId}_publicKey`]) && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: '#333',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            whiteSpace: 'nowrap',
                            zIndex: '1000',
                            marginBottom: '4px',
                            pointerEvents: 'none',
                            opacity: 1,
                            visibility: 'visible',
                            animation: 'fadeIn 0.2s ease-in-out'
                          }}
                          className="copy-tooltip"
                        >
                          {copiedStates[`${agent.agentId}_publicKey`] ? 'Copied!' : 'Copy Public Key'}
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: '0',
                            height: '0',
                            borderLeft: '4px solid transparent',
                            borderRight: '4px solid transparent',
                            borderTop: '4px solid #333'
                          }}></div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {(() => {
                    // Generate registry URL with proper format
                    const registryUrl = `https://nekuda-agent-registry.onrender.com/.well-known/http-message-signatures-directory/${agent.agentId}`;
                    
                    return (
                      <>
                        <a
                          href={registryUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#007bff',
                            textDecoration: 'underline',
                            fontSize: '12px',
                            fontFamily: 'monospace'
                          }}
                        >
                          {shortenRegistryUrl(registryUrl)}
                        </a>
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <button
                            onClick={() => handleCopyRegistryUrl(agent.agentId, registryUrl)}
                            onMouseEnter={() => setHoveredButtons(prev => ({ ...prev, [`${agent.agentId}_registryUrl`]: true }))}
                            onMouseLeave={() => setHoveredButtons(prev => ({ ...prev, [`${agent.agentId}_registryUrl`]: false }))}
                            style={{
                              background: copiedStates[`${agent.agentId}_registryUrl`] ? '#28a745'
                                         : hoveredButtons[`${agent.agentId}_registryUrl`] ? '#007bff' : 'none',
                              border: `1px solid ${copiedStates[`${agent.agentId}_registryUrl`] ? '#28a745'
                                         : hoveredButtons[`${agent.agentId}_registryUrl`] ? '#007bff' : '#ccc'}`,
                              borderRadius: '4px',
                              padding: '4px 8px',
                              cursor: 'pointer',
                              fontSize: '10px',
                              color: copiedStates[`${agent.agentId}_registryUrl`] || hoveredButtons[`${agent.agentId}_registryUrl`] ? 'white' : '#666',
                              minWidth: '40px',
                              whiteSpace: 'nowrap',
                              transition: 'all 0.2s ease-in-out',
                              position: 'relative'
                            }}
                            className="copy-button"
                          >
                            {copiedStates[`${agent.agentId}_registryUrl`] ? '✓' : 'Copy'}
                          </button>

                          {(hoveredButtons[`${agent.agentId}_registryUrl`] || copiedStates[`${agent.agentId}_registryUrl`]) && (
                            <div
                              style={{
                                position: 'absolute',
                                bottom: '100%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                backgroundColor: '#333',
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                whiteSpace: 'nowrap',
                                zIndex: '1000',
                                marginBottom: '4px',
                                pointerEvents: 'none',
                                opacity: 1,
                                visibility: 'visible',
                                animation: 'fadeIn 0.2s ease-in-out'
                              }}
                              className="copy-tooltip"
                            >
                              {copiedStates[`${agent.agentId}_registryUrl`] ? 'Copied!' : 'Copy Registry URL'}
                              <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '0',
                                height: '0',
                                borderLeft: '4px solid transparent',
                                borderRight: '4px solid transparent',
                                borderTop: '4px solid #333'
                              }}></div>
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Mobile Card View */}
      <div className="mobile-agent-cards">
        {currentAgents
          .filter(agent => agent && agent.agentId)
          .map(agent => {
            const registryUrl = `https://nekuda-agent-registry.onrender.com/.well-known/http-message-signatures-directory/${agent.agentId}`;
            
            return (
              <div key={agent.agentId} className="mobile-agent-card">
                <div className="mobile-card-field">
                  <div className="mobile-field-label">Agent ID</div>
                  <div className="mobile-field-value monospace">{agent.agentId}</div>
                  <div className="mobile-copy-container">
                    <button 
                      className={`mobile-copy-button ${copiedStates[`${agent.agentId}_agentId`] ? 'copied' : ''}`}
                      onClick={() => handleCopyAgentId(agent.agentId)}
                    >
                      {copiedStates[`${agent.agentId}_agentId`] ? '✓ Copied' : 'Copy ID'}
                    </button>
                  </div>
                </div>

                {agent.technical?.jwk?.x && (
                  <div className="mobile-card-field">
                    <div className="mobile-field-label">Public Key</div>
                    <div className="mobile-field-value monospace">{agent.technical.jwk.x}</div>
                    <div className="mobile-copy-container">
                      <button 
                        className={`mobile-copy-button ${copiedStates[`${agent.agentId}_publicKey`] ? 'copied' : ''}`}
                        onClick={() => handleCopyPublicKey(agent.agentId, agent.technical.jwk.x)}
                      >
                        {copiedStates[`${agent.agentId}_publicKey`] ? '✓ Copied' : 'Copy Key'}
                      </button>
                    </div>
                  </div>
                )}

                <div className="mobile-card-field">
                  <div className="mobile-field-label">Registry URL</div>
                  <div className="mobile-field-value monospace">
                    <a
                      href={registryUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#007bff',
                        textDecoration: 'underline'
                      }}
                    >
                      {shortenRegistryUrl(registryUrl)}
                    </a>
                  </div>
                  <div className="mobile-copy-container">
                    <button 
                      className={`mobile-copy-button ${copiedStates[`${agent.agentId}_registryUrl`] ? 'copied' : ''}`}
                      onClick={() => handleCopyRegistryUrl(agent.agentId, registryUrl)}
                    >
                      {copiedStates[`${agent.agentId}_registryUrl`] ? '✓ Copied' : 'Copy URL'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {/* Subtle Pagination Controls */}
      {totalPages > 1 && (
        <div style={{
          position: 'sticky',
          bottom: '15px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: '15px',
          padding: '8px 12px',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(3px)',
          borderRadius: '6px',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          zIndex: 10,
          maxWidth: 'fit-content',
          margin: '15px auto 0 auto',
          fontSize: '13px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontFamily: "'VT323', monospace",
            fontSize: '13px'
          }}>
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              style={{
                background: currentPage === 1 ? 'transparent' : 'rgba(0, 123, 255, 0.08)',
                color: currentPage === 1 ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 100, 200, 0.8)',
                border: 'none',
                borderRadius: '3px',
                padding: '4px 8px',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                fontWeight: 'normal',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (currentPage > 1) {
                  e.target.style.background = 'rgba(0, 123, 255, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage > 1) {
                  e.target.style.background = 'rgba(0, 123, 255, 0.08)';
                }
              }}
            >
              ‹
            </button>
            
            <span style={{ 
              color: 'rgba(0, 0, 0, 0.6)', 
              fontSize: '12px',
              padding: '0 4px'
            }}>
              {currentPage}/{totalPages}
            </span>
            
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              style={{
                background: currentPage === totalPages ? 'transparent' : 'rgba(0, 123, 255, 0.08)',
                color: currentPage === totalPages ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 100, 200, 0.8)',
                border: 'none',
                borderRadius: '3px',
                padding: '4px 8px',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                fontWeight: 'normal',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (currentPage < totalPages) {
                  e.target.style.background = 'rgba(0, 123, 255, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage < totalPages) {
                  e.target.style.background = 'rgba(0, 123, 255, 0.08)';
                }
              }}
            >
              ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const NekudaAgentRegistry = () => {
  const [view, setView] = useState('table'); // 'form' or 'table' or 'about' or 'verify' or 'modal'
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agents, setAgents] = useState([]); // Start with empty array

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showJWKDetails, setShowJWKDetails] = useState(false);

  // State for copy button functionality
  const [copiedStates, setCopiedStates] = useState({});
  const [hoveredButtons, setHoveredButtons] = useState({});

  const [openTooltip, setOpenTooltip] = useState(null);
  const [verificationData, setVerificationData] = useState({
    agentId: '',
    email: '',
    info: '',
  });
  const [showVerificationSuccess, setShowVerificationSuccess] = useState(false);

  const [activeSDKTab, setActiveSDKTab] = useState('agent-signing');
  const [isModalOpen, setIsModalOpen] = useState(false);

  // State management for modal copy functionality
  const [modalCopiedStates, setModalCopiedStates] = useState({});
  const [modalHoveredButtons, setModalHoveredButtons] = useState({});

  const [showKeyModal, setShowKeyModal] = useState(false);
  const [generatedKeys, setGeneratedKeys] = useState(null);
  const [registeredAgent, setRegisteredAgent] = useState(null);
  const [browserSupported, setBrowserSupported] = useState(true);
  const [error, setError] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const agentsPerPage = 15;

  // Check browser compatibility on mount
  useEffect(() => {
    const checkBrowserSupport = async () => {
      const supported = await isEd25519Supported();
      setBrowserSupported(supported);
    };
    checkBrowserSupport();
  }, []);

  // Handle navigation from modal to verify page
  useEffect(() => {
    const handleNavigateToVerify = (event) => {
      const { agentId } = event.detail;
      setView('verify');
      if (agentId) {
        setVerificationData(prev => ({ ...prev, agentId }));
      }
    };

    window.addEventListener('navigate-to-verify', handleNavigateToVerify);
    return () => {
      window.removeEventListener('navigate-to-verify', handleNavigateToVerify);
    };
  }, []);

  // Fetch agents from API on component mount
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const response = await fetch('/v1/agents');
        if (response.ok) {
          const data = await response.json();
          setAgents(data);
        } else {
          console.error('Failed to fetch agents:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching agents:', error);
      }
    };

    fetchAgents();
  }, []);

  // Generate agent ID
  const generateAgentId = () => {
    const randomHex = Math.random().toString(16).substring(2, 10);
    return `AG_${randomHex}`;
  };

  const [agentId, setAgentId] = useState(generateAgentId());

  // Form validation - simplified for no fields
  const validateForm = () => {
    // No form fields to validate in simplified version
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    if (!browserSupported) {
      setError('Your browser does not support the required cryptographic operations. Please use a modern browser.');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Step 1: Generate agent ID
      const agentId = generateAgentId();
      
      // Step 2: Generate keys client-side
      const keyMaterial = await generateAgentKeys(agentId);
      
      // Step 3: Prepare agent data with public JWK - simplified with auto-generated values
      const agentData = {
        agentId,
        profile: {
          name: `Agent ${agentId}`,
          description: "Auto-generated agent for Web Bot Auth",
          category: "other",
          developer: "Auto-generated",
          contact: "auto@example.com",
          status: "active"
        },
        technical: {
          jwk: keyMaterial.publicJWK, // Only send public key
          protocols: ["http-message-signatures"]
        },
        metadata: {
          tags: [],
          website: "",
          documentation: "",
          webhooks: [],
          geoRegions: []
        },
        verification: {
          verified: false,
          verificationDate: "",
          verifierId: "",
          verifierSignature: "",
          verificationNotes: "",
          submissionDate: new Date().toISOString()
        },
        // Include directory signature for HTTP Message Signatures Directory Draft compliance
        directorySignature: keyMaterial.directorySignature
      };

      // Step 4: Register agent with server
      const response = await fetch('/v1/agents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(agentData)
      });

      console.log('Request URL:', '/v1/agents');
      console.log('Request method:', 'POST');
      console.log('Request body:', JSON.stringify(agentData, null, 2));
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to register agent');
      }

      const result = await response.json();
      console.log('Raw response from server:', result);
      
      // Debug logging
      console.log('Server response:', result);
      console.log('Agent data:', result.agent);
      
      // Handle both wrapped and unwrapped responses
      const savedAgentData = result.agent || result; // Use result.agent if it exists, otherwise use result directly
      
      // Step 5: Show success modal with key management
      setRegisteredAgent(savedAgentData);
      setGeneratedKeys(keyMaterial);
      
      // Only show modal if we have valid data
      if (savedAgentData && keyMaterial) {
        console.log('About to show key modal');
        console.log('showKeyModal state before:', showKeyModal);
        setShowKeyModal(true);
        console.log('setShowKeyModal(true) called');
      } else {
        console.error('Missing agent data or key material for modal');
        console.log('savedAgentData:', savedAgentData);
        console.log('keyMaterial:', keyMaterial);
      }
      
      // Update local agent list
      setAgents(prev => [...prev, savedAgentData]);
      
      // Reset form - no form fields to reset in simplified version
      setAgentId(generateAgentId());
    } catch (error) {
      console.error('Registration failed:', error);
      setError(`Registration failed: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Copy directory URL function
  const handleCopyDirectoryUrl = () => {
    const directoryUrl = 'https://nekuda-agent-registry.onrender.com/.well-known/http-message-signatures-directory';
    navigator.clipboard.writeText(directoryUrl).then(() => {
      setCopiedStates(prev => ({ ...prev, 'directoryUrl': true }));
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, 'directoryUrl': false }));
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy directory URL:', err);
    });
  };

  // Handle URL parameters for agent viewing and verification
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const agentId = urlParams.get('agent');
    const viewParam = urlParams.get('view');
    const verifyAgentId = urlParams.get('agentId');
    
    if (agentId) {
      const agent = agents.find(a => a.agentId === agentId);
      if (agent) {
        handleOpenModal(agent);
      }
    }
    
    // Handle verification page with pre-filled agentId
    if (viewParam === 'verify') {
      setView('verify');
      if (verifyAgentId) {
        setVerificationData(prev => ({ ...prev, agentId: verifyAgentId }));
      }
    }
  }, [agents]);

  // Add fonts
  useEffect(() => {
    const link1 = document.createElement('link');
    link1.href = 'https://fonts.googleapis.com/css2?family=VT323&display=swap';
    link1.rel = 'stylesheet';
    document.head.appendChild(link1);

    const link2 = document.createElement('link');
    link2.href = 'https://fonts.googleapis.com/css2?family=Rubik:wght@300;400;500&display=swap';
    link2.rel = 'stylesheet';
    document.head.appendChild(link2);

    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateX(-50%) translateY(5px);
        }
        to {
          opacity: 1;
          transform: translateX(-50%) translateY(0);
        }
      }
      
      @keyframes blink {
        0% { opacity: 0.4; }
        50% { opacity: 1; }
        100% { opacity: 0.4; }
      }
      
      .status-dot {
        display: inline-block;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        margin-right: 8px;
      }
      
      .status-dot.active {
        background-color: #2ecc71;
        box-shadow: 0 0 4px rgba(46, 204, 113, 0.5);
        animation: blink 1.2s ease-in-out infinite;
      }
      
      .status-dot.inactive {
        display: none;
      }
      
      .status-dot.pending {
        background-color: #ffc107;
      }
      
      .with-tooltip {
        position: relative;
        display: inline-block;
      }
      
      .tooltip-content {
        visibility: hidden;
        width: 250px;
        background-color: black;
        color: #fff;
        text-align: left;
        border-radius: 6px;
        padding: 10px;
        position: absolute;
        z-index: 1;
        bottom: 150%;
        left: 50%;
        margin-left: -125px;
        opacity: 0;
        transition: opacity 0.3s;
        font-family: 'Rubik', sans-serif;
        font-size: 14px;
        line-height: 1.4;
      }
      .with-tooltip:hover .tooltip-content {
        visibility: visible;
        opacity: 1;
      }
      .with-tooltip .tooltip-content a {
        color: #00aaff;
        text-decoration: none;
      }
       .with-tooltip .tooltip-content a:hover {
        text-decoration: underline;
      }
      .tooltip-container {
        position: relative;
        display: flex;
        align-items: center;
      }
      .tooltip-icon {
        cursor: pointer;
        margin-left: 8px;
        font-weight: bold;
        color: #007bff;
        display: inline-block;
        font-family: 'VT323', monospace;
        font-size: 22px;
      }
      .tooltip-content-click {
        visibility: visible;
        opacity: 1;
        width: 280px;
        background-color: #333;
        color: #fff;
        text-align: left;
        border-radius: 6px;
        padding: 15px;
        position: absolute;
        z-index: 10;
        bottom: 130%;
        left: -120px;
        box-shadow: 0 4px 8px rgba(0,0,0,0.2);
        font-family: 'Rubik', sans-serif;
        font-size: 14px;
        line-height: 1.5;
      }
      .tooltip-content-click .tooltip-close {
        position: absolute;
        top: 5px;
        right: 10px;
        cursor: pointer;
        font-size: 24px;
        font-weight: bold;
        color: #fff;
        line-height: 1;
      }
      .tooltip-content-click .tooltip-close:hover {
        color: #ccc;
      }
      .tooltip-content-click .tooltip-title {
        margin-right: 20px; /* Space for the close button */
      }
      .tooltip-content-click::after { /* Arrow */
        content: "";
        position: absolute;
        top: 100%;
        left: 135px;
        margin-left: -5px;
        border-width: 5px;
        border-style: solid;
        border-color: #333 transparent transparent transparent;
      }
      .tooltip-content-click p {
        margin: 10px 0 15px;
      }
      .tooltip-content-click a {
        color: #61dafb;
        text-decoration: underline;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(link1);
      document.head.removeChild(link2);
      document.head.removeChild(style);
    };
  }, []);

  const handleRegisterMore = () => {
    setView('form');
  };
  
  const handleTooltipToggle = (tooltipId) => {
    setOpenTooltip(openTooltip === tooltipId ? null : tooltipId);
  };

  const handleOpenModal = (agent) => {
    setSelectedAgent(agent);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAgent(null);
    window.history.pushState({}, '', window.location.pathname);
  };

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isModalOpen) {
        handleCloseModal();
      }
    };
    
    if (isModalOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'auto';
    };
  }, [isModalOpen]);

  const handleVerificationInputChange = (e) => {
    const { name, value } = e.target;
    setVerificationData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Copy handlers for modal
  const handleCopyJWK = (agentId, jwk) => {
    if (!jwk) return;

    navigator.clipboard.writeText(JSON.stringify(jwk, null, 2)).then(() => {
      setModalCopiedStates(prev => ({ ...prev, [`${agentId}_jwk`]: true }));
      setTimeout(() => {
        setModalCopiedStates(prev => ({ ...prev, [`${agentId}_jwk`]: false }));
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy JWK:', err);
    });
  };


  const handleVerificationSubmit = async () => {
    if (!verificationData.agentId || !verificationData.email) {
      alert('Please fill in Agent ID and Contact Email.');
      return;
    }
    
    setIsSubmitting(true);
    setShowVerificationSuccess(true);
    
    try {
      const response = await fetch(`/v1/agents/${verificationData.agentId}/verification-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: verificationData.email,
          info: verificationData.info
        }),
      });

      if (response.ok) {
        setTimeout(() => {
          setShowVerificationSuccess(false);
          setView('form');
          setVerificationData({ agentId: '', email: '', info: '' });
        }, 2000);
      } else {
        console.error('Failed to submit verification request:', response.statusText);
        setShowVerificationSuccess(false);
        alert('Failed to submit verification request. Please check the agent ID and try again.');
      }
    } catch (error) {
      console.error('Error submitting verification request:', error);
      setShowVerificationSuccess(false);
      alert('Error submitting verification request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="nekuda-container">
      <style>{`
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        .nekuda-container {
          min-height: 100vh;
          background-color: #eef0f2;
          background-image: repeating-linear-gradient(#e5e7eb 0px, #e5e7eb 1px, transparent 1px, transparent 2px);
          position: relative;
          font-family: 'VT323', monospace;
          padding: 20px;
        }

        /* CRT Effect */
        .nekuda-container::before {
          content: '';
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(rgba(18, 16, 16, 0) 50%, rgba(0, 0, 0, 0.1) 50%);
          background-size: 100% 2px;
          pointer-events: none;
          z-index: 4;
        }

        /* Header */
        .header {
          position: fixed;
          top: 20px;
          left: 20px;
          right: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          z-index: 100;
        }

        .dropdown-btn {
          font-family: 'VT323', monospace;
          font-size: 18px;
          text-transform: uppercase;
          background: transparent;
          border: none;
          color: rgba(0, 0, 0, 0.7);
          cursor: pointer;
          position: relative;
          padding: 5px 10px;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .dropdown-btn:hover {
          color: black;
        }

        .dropdown-menu {
          position: relative;
        }

        .dropdown-content {
          display: none;
          position: absolute;
          right: 0;
          background-color: #f1f1f1;
          min-width: 160px;
          box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
          z-index: 1;
        }

        .dropdown-content.show {
          display: block;
        }

        .dropdown-menu:hover .dropdown-content {
          display: block;
        }

        .dropdown-menu:hover .dropdown-arrow {
          transform: rotate(180deg);
        }

        .dropdown-content button {
          color: black;
          padding: 12px 16px;
          text-decoration: none;
          display: block;
          width: 100%;
          text-align: left;
          font-size: 22px;
        }

        .dropdown-content button:hover {
          background-color: #ddd;
        }

        .dropdown-item {
          font-family: 'VT323', monospace;
          text-transform: uppercase;
          background: transparent;
          border: none;
          color: rgba(0, 0, 0, 0.7);
          cursor: pointer;
          position: relative;
          padding: 12px 16px;
          transition: all 0.3s ease;
          width: 100%;
          text-align: left;
          font-size: 22px;
        }

        .dropdown-item:hover {
          color: black;
          background-color: #ddd;
        }

        /* Dropdown arrow */
        .dropdown-arrow {
          font-size: 12px;
          margin-left: 8px;
          transition: transform 0.3s ease;
          display: inline-block;
        }



        .coming-soon {
          font-size: 12px;
          color: rgba(0, 0, 0, 0.5);
          white-space: nowrap;
          margin-left: 8px;
        }

        /* Main Content */
        .main-content {
          display: flex;
          max-width: 1400px;
          margin: 0 auto;
          padding: 20px 20px 20px;
          gap: 40px;
          position: relative;
          z-index: 10;
        }

        .page-title {
          font-family: 'VT323', monospace;
          font-size: 1.6rem;
          text-align: center;
          color: black;
          margin: 40px 0 20px;
          text-transform: uppercase;
          letter-spacing: 4px;
        }

        /* Form Panel */
        .form-panel {
          flex: 1;
          background-color: #ffffff;
          padding: 40px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
        }

        .form-title {
          font-family: 'VT323', monospace;
          font-size: 32px;
          text-transform: uppercase;
          letter-spacing: 2px;
          color: black;
          margin-bottom: 30px;
          text-align: center;
        }

        /* Form Groups */
        .form-group {
          margin-bottom: 20px;
        }

        .form-label {
          display: block;
          font-family: 'VT323', monospace;
          font-size: 18px;
          color: rgba(0, 0, 0, 0.8);
          margin-bottom: 5px;
          text-transform: uppercase;
        }

        .form-input, .form-select, .form-textarea {
          width: 100%;
          font-family: 'VT323', monospace;
          font-size: 20px;
          padding: 10px 15px;
          border: 1px solid #d1d5db;
          background-color: #fff;
          color: black;
          transition: all 0.2s ease;
          border-radius: 4px;
        }

        .form-input:hover, .form-select:hover, .form-textarea:hover {
          border-color: #a0aec0;
        }

        .form-input:focus, .form-select:focus, .form-textarea:focus {
          outline: none;
          border-color: #00C8FF;
          box-shadow: 0 0 0 3px rgba(0, 200, 255, 0.2);
        }

        .form-textarea {
          resize: vertical;
          min-height: 80px;
        }

        .char-count {
          font-size: 14px;
          color: rgba(0, 0, 0, 0.6);
          text-align: right;
          margin-top: 2px;
        }

        .error {
          color: #ff0000;
          font-size: 14px;
          margin-top: 2px;
        }

        /* Checkbox Group */
        .checkbox-group {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-top: 10px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 18px;
          color: rgba(0, 0, 0, 0.7);
          cursor: pointer;
        }

        .checkbox-label input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        /* Key Upload */
        .key-upload {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .upload-btn {
          font-family: 'VT323', monospace;
          font-size: 18px;
          padding: 10px 20px;
          background-color: transparent;
          border: 2px solid rgba(0, 0, 0, 0.3);
          color: rgba(0, 0, 0, 0.7);
          cursor: pointer;
          transition: all 0.3s ease;
          text-transform: uppercase;
        }

        .upload-btn:hover {
          border-color: black;
          color: black;
        }

        .tooltip {
          font-size: 14px;
          color: rgba(0, 0, 0, 0.6);
          font-style: italic;
        }

        /* Submit Button */
        .submit-button {
          font-family: 'VT323', monospace;
          font-size: 24px;
          letter-spacing: 2px;
          background-color: #00C8FF;
          border: none;
          color: white;
          padding: 15px 40px;
          margin-top: 30px;
          cursor: pointer;
          transition: all 0.3s ease;
          text-transform: uppercase;
          display: block;
          width: 100%;
          position: relative;
          overflow: hidden;
          border-radius: 8px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
        }

        .submit-button:hover {
          background-color: #00a2cc;
          color: white;
        }

        .submit-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Preview Panel */
        .preview-panel {
          flex: 1;
          position: sticky;
          top: 80px;
          height: fit-content;
        }

        .preview-title {
          font-family: 'VT323', monospace;
          font-size: 24px;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: black;
          margin-bottom: 20px;
        }

        .code-preview {
          background-color: #1e293b;
          color: #d4d4d4;
          padding: 20px;
          border-radius: 8px;
          font-family: 'Courier New', monospace;
          font-size: 14px;
          line-height: 1.6;
          overflow-x: auto;
          max-height: 70vh;
          overflow-y: auto;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }

        .json-syntax {
          white-space: pre-wrap;
          word-wrap: break-word;
        }

        /* Registration Card Styles */
        .registration-card {
          background-color: #ffffff;
          padding: 24px;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .card-field {
          margin-bottom: 24px;
        }

        .card-field:last-child {
          margin-bottom: 0;
        }

        .card-label {
          display: block;
          font-family: 'VT323', monospace;
          font-size: 16px;
          font-weight: bold;
          color: #1f2937;
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .card-value {
          background-color: #f8fafc;
          padding: 12px 16px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .agent-id-display {
          font-family: 'Courier New', monospace;
          font-size: 14px;
          font-weight: bold;
          color: #2563eb;
          background-color: #dbeafe;
          padding: 4px 8px;
          border-radius: 4px;
          border: 1px solid #93c5fd;
        }

        .tech-detail {
          margin-bottom: 8px;
          font-size: 14px;
          color: #374151;
        }

        .tech-detail:last-child {
          margin-bottom: 0;
        }

        .tech-detail strong {
          color: #1f2937;
          margin-right: 8px;
        }

        .jwk-toggle-button {
          background: none;
          border: none;
          color: #2563eb;
          cursor: pointer;
          font-family: 'VT323', monospace;
          font-size: 16px;
          font-weight: bold;
          text-transform: uppercase;
          letter-spacing: 1px;
          padding: 0;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: color 0.2s ease;
        }

        .jwk-toggle-button:hover {
          color: #1d4ed8;
        }

        .jwk-preview {
          margin-top: 8px;
        }

        .jwk-json {
          background-color: #1e293b;
          color: #d4d4d4;
          padding: 12px;
          border-radius: 6px;
          font-family: 'Courier New', monospace;
          font-size: 12px;
          line-height: 1.4;
          margin-bottom: 8px;
          overflow-x: auto;
        }

        .jwk-note {
          font-size: 12px;
          color: #6b7280;
          font-style: italic;
        }

        /* Success Message */
        .success-message {
          position: fixed;
          bottom: 40px;
          left: 50%;
          transform: translateX(-50%);
          color: white;
          background-color: #000;
          padding: 12px 30px;
          border-radius: 50px;
          font-family: 'VT323', monospace;
          font-size: 20px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
          z-index: 9999;
          animation: disneyBounce 0.25s forwards;
        }

        .success-message.success {
          background-color: #00C8FF;
        }

        @keyframes disneyBounce {
          0% { 
            opacity: 0;
            transform: translateX(-50%) translateY(80px) scale(0.8);
          }
          25% { 
            opacity: 1;
            transform: translateX(-50%) translateY(-20px) scale(1.15);
          }
          50% { 
            transform: translateX(-50%) translateY(10px) scale(0.95);
          }
          75% { 
            transform: translateX(-50%) translateY(-5px) scale(1.02);
          }
          100% { 
            transform: translateX(-50%) translateY(0) scale(1);
          }
        }

        /* Auto-detected label */
        .auto-detected {
          font-size: 14px;
          color: rgba(0, 0, 0, 0.6);
          font-style: italic;
          margin-left: 10px;
        }

        /* Note text */
        .note-text {
          font-size: 14px;
          color: rgba(0, 0, 0, 0.6);
          margin-top: 10px;
          line-height: 1.4;
        }

        .agent-table-container { 
          width: 100%; max-width: 900px; margin: 20px auto; 
          background: transparent; padding: 10px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        /* Desktop: hide mobile cards, show table */
        .mobile-agent-cards {
          display: none;
        }
        .agent-table { width: auto; min-width: 600px; max-width: 800px; border-collapse: collapse; margin-bottom: 15px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); background: white; border-radius: 8px; overflow: hidden; margin-left: auto; margin-right: auto; }
        .agent-table th, .agent-table td {
          padding: 10px 12px; text-align: left; font-family: 'Rubik', sans-serif;
          border-bottom: 1px solid rgba(0,0,0,0.1);
        }
        .agent-table th:nth-child(7), .agent-table td:nth-child(7) {
          min-width: 120px; width: 120px;
        }
        .agent-table th { font-family: 'VT323', monospace; font-size: 20px; text-transform: uppercase; color: black; }
        .agent-table td { font-size: 16px; color: rgba(0,0,0,0.8); }
        .agent-table tr:last-child td { border-bottom: none; }
        .agent-table tr:hover { background-color: rgba(221, 221, 221, 0.3); }
        .status-dot {
          height: 8px; width: 8px; background-color: #5cb85c; border-radius: 50%;
          display: inline-block; margin-right: 8px; animation: blink 1.5s infinite;
          vertical-align: middle;
        }
        .status-dot.pending { background-color: #f0ad4e; }
        .status-dot.inactive { background-color: #d9534f; animation: none; opacity: 0.5; }
        
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0.7; }
        }

        @keyframes fadeIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        .verification-badge {
          padding: 6px 12px; border-radius: 12px; font-size: 14px; font-weight: bold;
          display: inline-flex; align-items: center; font-family: 'Rubik', sans-serif;
          white-space: nowrap; min-width: fit-content;
        }
        .verification-badge.verified {
          background-color: #d4edda; color: #155724; border: 1px solid #c3e6cb;
        }
        .verification-badge.unverified {
          background-color: #f8d7da; color: #721c24; border: 1px solid #f5c6cb;
        }

        @media (max-width: 968px) {
          .main-content {
            flex-direction: column;
          }
          
          .preview-panel {
            position: relative;
            top: 0;
          }
          .agent-table-container { padding: 20px; margin-top: 40px; }
        }

        /* Improved touch targets for mobile */
        @media (max-width: 768px) {
          .copy-button {
            min-height: 36px !important;
            padding: 8px 12px !important;
            font-size: 12px !important;
          }
          
          .dropdown-btn, .dropdown-item {
            min-height: 44px;
            display: flex;
            align-items: center;
          }
        }

        /* Enhanced Mobile Responsiveness */
        @media (max-width: 768px) {
          .nekuda-container {
            padding: 10px 0;
          }

          .header {
            position: fixed;
            top: 10px;
            left: 10px;
            right: 10px;
            flex-wrap: wrap;
            gap: 10px;
          }

          .dropdown-menu {
            position: relative;
          }

          .dropdown-content {
            right: -10px;
            min-width: 200px;
            box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.3);
          }

          .dropdown-btn {
            font-size: 20px;
            padding: 8px 12px;
          }

          .dropdown-item {
            font-size: 20px;
            padding: 15px 20px;
          }

          .page-title {
            font-size: 1.3rem;
            margin: 30px 0 15px;
            letter-spacing: 2px;
            padding: 0 10px;
          }

          .main-content {
            padding: 10px 0;
            gap: 20px;
            max-width: 100%;
          }

          .form-panel {
            padding: 20px 10px;
            margin: 0;
          }

          .form-title {
            font-size: 26px;
            margin-bottom: 20px;
          }

          .form-label {
            font-size: 16px;
            margin-bottom: 8px;
          }

          .form-input, .form-select, .form-textarea {
            font-size: 18px;
            padding: 12px 15px;
          }

          .form-textarea {
            min-height: 100px;
          }

          .submit-button {
            font-size: 20px;
            padding: 15px 30px;
            margin-top: 20px;
          }

          .preview-panel {
            margin-top: 0;
          }

          .preview-title {
            font-size: 20px;
            text-align: center;
          }

          .code-preview {
            font-size: 12px;
            padding: 15px;
            max-height: 50vh;
          }

          /* Mobile tooltip adjustments */
          .tooltip-content-click {
            width: 260px;
            left: -50px;
            transform: none;
            bottom: auto;
            top: 120%;
            font-size: 13px;
            padding: 12px;
          }

          .tooltip-content-click::after {
            top: -5px;
            bottom: auto;
            border-color: transparent transparent #333 transparent;
          }

          .tooltip-icon {
            font-size: 20px;
          }

          /* Mobile key upload section */
          .key-upload {
            flex-direction: column;
          }

          .upload-btn {
            font-size: 16px;
            padding: 12px 20px;
            width: 100%;
          }

          /* Mobile checkbox adjustments */
          .checkbox-group {
            gap: 15px;
          }

          .checkbox-label {
            font-size: 16px;
            flex-wrap: wrap;
          }

          /* Mobile agent table - Card Layout */
          .agent-table-container {
            padding: 10px;
            margin: 10px 0;
          }

          .agent-table {
            display: none; /* Hide table on mobile */
          }

          /* Mobile card layout */
          .mobile-agent-cards {
            display: block;
          }

          .mobile-agent-card {
            background: white;
            border-radius: 12px;
            padding: 20px;
            margin-bottom: 16px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            border: 1px solid #e9ecef;
          }

          .mobile-card-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 15px;
            flex-wrap: wrap;
            gap: 10px;
          }

          .mobile-agent-name {
            font-family: 'VT323', monospace;
            font-size: 20px;
            font-weight: bold;
            color: #333;
            margin: 0;
          }

          .mobile-verification-badge {
            padding: 4px 12px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: bold;
            white-space: nowrap;
          }

          .mobile-card-field {
            margin-bottom: 12px;
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .mobile-field-label {
            font-family: 'VT323', monospace;
            font-size: 14px;
            color: #666;
            text-transform: uppercase;
            margin: 0;
          }

          .mobile-field-value {
            font-family: 'Rubik', sans-serif;
            font-size: 15px;
            color: #333;
            word-break: break-all;
            line-height: 1.4;
          }

          .mobile-field-value.monospace {
            font-family: 'Courier New', monospace;
            font-size: 13px;
            background: #f8f9fa;
            padding: 8px;
            border-radius: 4px;
          }

          .mobile-copy-container {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-top: 4px;
          }

          .mobile-copy-button {
            background: #007bff;
            color: white;
            border: none;
            padding: 8px 16px;
            border-radius: 6px;
            font-size: 12px;
            cursor: pointer;
            min-height: 36px;
            font-family: 'VT323', monospace;
            text-transform: uppercase;
          }

          .mobile-copy-button:active {
            background: #0056b3;
          }

          .mobile-copy-button.copied {
            background: #28a745;
          }

          .mobile-status-container {
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .mobile-view-button {
            background: none;
            border: 1px solid #007bff;
            color: #007bff;
            padding: 8px 16px;
            border-radius: 6px;
            font-size: 12px;
            cursor: pointer;
            min-height: 36px;
            font-family: 'VT323', monospace;
            text-transform: uppercase;
          }

          .mobile-view-button:active {
            background: #007bff;
            color: white;
          }

          /* Mobile success message */
          .success-message {
            bottom: 20px;
            left: 10px;
            right: 10px;
            transform: none;
            font-size: 18px;
            padding: 15px 25px;
            text-align: center;
          }

          /* Mobile about page adjustments */
          .form-panel h3 {
            font-size: 18px !important;
          }

          .form-panel p, .form-panel li {
            font-size: 15px !important;
          }

          .form-panel ul {
            margin-left: 15px !important;
          }

          /* Mobile note text */
          .note-text {
            font-size: 13px;
            line-height: 1.5;
          }

          .char-count {
            font-size: 13px;
          }

          .error {
            font-size: 13px;
          }

          .auto-detected {
            font-size: 13px;
          }
        }

        /* Extra small mobile devices */
        @media (max-width: 480px) {
          .header {
            top: 5px;
            left: 5px;
            right: 5px;
          }

          .page-title {
            font-size: 1.1rem;
            margin: 25px 0 15px;
            letter-spacing: 1px;
          }

          .main-content {
            padding: 5px;
            gap: 15px;
          }

          .form-panel {
            padding: 15px;
          }

          .form-title {
            font-size: 22px;
            margin-bottom: 15px;
          }

          .form-input, .form-select, .form-textarea {
            font-size: 16px;
            padding: 10px 12px;
          }

          .submit-button {
            font-size: 18px;
            padding: 12px 25px;
          }

          .code-preview {
            font-size: 11px;
            padding: 12px;
          }

          .tooltip-content-click {
            width: 240px;
            left: -60px;
            font-size: 12px;
            padding: 10px;
          }

          .agent-table-container {
            padding: 5px;
            margin: 15px auto 10px;
          }

          .agent-table th {
            font-size: 14px;
            padding: 8px 6px;
          }

          .agent-table td {
            font-size: 12px;
            padding: 8px 6px;
          }

          .success-message {
            font-size: 16px;
            padding: 12px 20px;
            bottom: 15px;
            left: 5px;
            right: 5px;
          }

          .dropdown-btn {
            font-size: 18px;
            padding: 6px 10px;
          }

          .dropdown-item {
            font-size: 18px;
            padding: 12px 16px;
          }

          .dropdown-content {
            min-width: 180px;
          }
        }

        @keyframes blink {
          0% { opacity: 1; } 50% { opacity: 0; } 100% { opacity: 1; }
        }

        .access-dot {
          animation: blink 1s infinite;
          width: 10px;
          height: auto;
          transition: all 0.7s cubic-bezier(0.86, 0, 0.07, 1);
          position: relative;
          z-index: 1;
          display: inline-block;
          margin-right: 12px;
          vertical-align: middle;
        }

        .form-title-container {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
        }

        .form-title-container .form-title {
          margin: 0;
        }

        /* Modal Mobile Responsiveness */
        @media (max-width: 768px) {
          .modal-overlay {
            padding: 15px !important;
          }

          .modal-content {
            padding: 20px !important;
            width: 95% !important;
            max-height: 90vh !important;
            overflow-y: auto !important;
          }

          .modal-title {
            font-size: 24px !important;
            margin-bottom: 15px !important;
          }

          .modal-warning {
            padding: 12px !important;
            font-size: 13px !important;
            margin-bottom: 15px !important;
          }

          .modal-key-group {
            margin-bottom: 15px !important;
          }

          .modal-label {
            font-size: 15px !important;
            margin-bottom: 6px !important;
          }

          .modal-key-container {
            flex-direction: column !important;
            align-items: stretch !important;
            padding: 8px !important;
          }

          .modal-key-text {
            font-size: 12px !important;
            margin-bottom: 10px !important;
          }

          .modal-button {
            font-size: 18px !important;
            padding: 10px 20px !important;
            margin: 15px auto 0 !important;
          }

          .modal-copy-button {
            margin-left: 0 !important;
            margin-top: 8px !important;
            width: 100% !important;
            padding: 8px 12px !important;
            font-size: 14px !important;
          }
        }

        @media (max-width: 480px) {
          .modal-overlay {
            padding: 10px !important;
          }

          .modal-content {
            padding: 15px !important;
            width: 98% !important;
          }

          .modal-title {
            font-size: 20px !important;
            margin-bottom: 10px !important;
          }

          .modal-warning {
            padding: 10px !important;
            font-size: 12px !important;
            margin-bottom: 12px !important;
          }

          .modal-key-group {
            margin-bottom: 12px !important;
          }

          .modal-label {
            font-size: 14px !important;
            margin-bottom: 5px !important;
          }

          .modal-key-text {
            font-size: 11px !important;
            margin-bottom: 8px !important;
          }

          .modal-button {
            font-size: 16px !important;
            padding: 8px 15px !important;
            margin: 12px auto 0 !important;
          }

          .modal-copy-button {
            padding: 6px 10px !important;
            font-size: 12px !important;
          }
        }
      `}</style>

      {/* Header */}
      <div className="header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <img 
            src="/assets/images/logo-2.svg" 
            alt="Nekuda Logo" 
            style={{ width: '40px', cursor: 'pointer' }} 
            onClick={() => window.open('https://nekuda.ai', '_blank')}
          />
          <button 
            onClick={() => setView('form')}
            style={{
              fontFamily: "'VT323', monospace",
              fontSize: '16px',
              textTransform: 'uppercase',
              background: '#007bff',
              color: 'white',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
          >
            Register Agent
          </button>
        </div>
        <div className="dropdown-menu">
          <button className="dropdown-btn" style={{fontSize: '22px'}}>
            <span className="dropdown-arrow">▼</span>
            Menu
          </button>
          <div className="dropdown-content">
          <button className="dropdown-item" onClick={() => setView('about')}>
              About
            </button>
            <button className="dropdown-item" onClick={() => setView('table')}>
              nekuda Registry
            </button>
            <button className="dropdown-item" onClick={() => setView('form')}>
              Register Agent
            </button>
            <button className="dropdown-item" onClick={() => setView('sdk')}>
              Resources
            </button>
          </div>
        </div>
      </div>

      <h1 className="page-title" onClick={() => setView('table')} style={{ cursor: 'pointer' }}>Nekuda Registry</h1>

      {/* Registry Description - Only show on main registry page */}
      {view === 'table' && (
        <div style={{
          maxWidth: '800px',
          margin: '20px auto 30px auto',
          padding: '0 20px',
          fontFamily: "'Rubik', sans-serif",
          fontSize: '16px',
          lineHeight: '1.6',
          color: 'rgba(0,0,0,0.8)',
          textAlign: 'left'
        }}>
          <p style={{ marginBottom: '15px' }}>
            By registering your agent, you enable websites and platforms to verify its identity using open cryptographic standards, allowing them to remove CAPTCHAs and blockers for smoother agent browsing.{' '}
            <button 
              onClick={() => setView('about')}
              style={{
                background: 'none',
                border: 'none',
                color: '#007bff',
                textDecoration: 'underline',
                cursor: 'pointer',
                padding: 0,
                font: 'inherit'
              }}
            >
              Read more here
            </button>.
          </p>
          <p style={{ 
            marginBottom: '15px', 
            fontSize: '14px', 
            color: 'rgba(0,0,0,0.6)',
            fontStyle: 'italic'
          }}>
            Verifiers can query the directory here:{' '}
            <a 
              href="https://nekuda-agent-registry.onrender.com/.well-known/http-message-signatures-directory"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#007bff',
                textDecoration: 'none'
              }}
            >
              https://nekuda-agent-registry.onrender.com/.well-known/http-message-signatures-directory
            </a>
          </p>
        </div>
      )}

      {/* Main Content */}
      {view === 'form' ? (
        <div className="main-content">
          <div className="form-panel">
            <div className="form-title-container">
              <img src="/assets/images/dot.png" alt="Blinking Dot" className="access-dot" />
              <h2 className="form-title">Register Your Agent</h2>
            </div>
            
            <div style={{
              fontFamily: "'Rubik', sans-serif", 
              marginBottom: '25px',
              textAlign: 'left'
            }}>
              <h3 style={{ marginBottom: '15px', fontSize: '18px', fontWeight: '600', textAlign: 'center' }}>What happens when you register?</h3>
              <ol style={{ 
                paddingLeft: '20px', 
                marginBottom: '20px',
                fontSize: '15px',
                lineHeight: '1.7',
                color: 'rgba(0,0,0,0.8)'
              }}>
                <li style={{ marginBottom: '8px' }}>We generate a secure Ed25519 key pair directly in your browser.</li>
                <li style={{ marginBottom: '8px' }}>The private key is never stored or shared — it's created client-side. Make sure to save it.</li>
                <li style={{ marginBottom: '8px' }}>Your agent is published to the nekuda Registry with a unique URL.</li>
                <li style={{ marginBottom: '8px' }}>You can use the general directory URL to register with Cloudflare's Verified Bots Program and other services.</li>
                <li style={{ marginBottom: '15px' }}>
                  <strong>Directory URL</strong>
                  <div style={{
                    marginTop: '8px',
                    padding: '12px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    maxWidth: '100%',
                    overflow: 'hidden'
                  }}>
                                      <span style={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    marginRight: '10px',
                    flex: 1
                  }}>
                    https://nekuda-agent-registry.onrender.com/.well-known/http-message-signatures-directory
                  </span>
                  <button
                    onClick={handleCopyDirectoryUrl}
                    onMouseEnter={() => setHoveredButtons(prev => ({ ...prev, 'directoryUrl': true }))}
                    onMouseLeave={() => setHoveredButtons(prev => ({ ...prev, 'directoryUrl': false }))}
                    style={{
                      background: copiedStates['directoryUrl'] ? '#28a745'
                                 : hoveredButtons['directoryUrl'] ? '#007bff' : 'none',
                      border: `1px solid ${copiedStates['directoryUrl'] ? '#28a745'
                                 : hoveredButtons['directoryUrl'] ? '#007bff' : '#ccc'}`,
                      borderRadius: '4px',
                      padding: '6px 10px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      color: copiedStates['directoryUrl'] || hoveredButtons['directoryUrl'] ? 'white' : '#666',
                      minWidth: '40px',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.2s ease-in-out',
                      flexShrink: 0
                    }}
                    title={copiedStates['directoryUrl'] ? 'Copied!' : 'Copy to clipboard'}
                  >
                    {copiedStates['directoryUrl'] ? '✓' : 'Copy'}
                  </button>
                  </div>
                </li>
              </ol>
              <p style={{ 
                fontSize: '15px',
                fontStyle: 'italic',
                color: 'rgba(0,0,0,0.7)',
                textAlign: 'center'
              }}>
                This helps your agent browse the web more smoothly - reducing friction like CAPTCHAs and making it easier for websites to recognize and trust it.
              </p>
            </div>
            
            <div>







              {/* Submit Button */}
              <button 
                type="button"
                className="submit-button"
                disabled={isSubmitting}
                onClick={handleSubmit}
              >
                {isSubmitting ? 'Submitting...' : 'Register Agent'}
              </button>
            </div>
          </div>

          {/* Registration Card Panel */}
          <div className="preview-panel">
            <h3 className="preview-title">Agent Registration Card</h3>
            <div className="registration-card">
              
              {/* Agent ID Field */}
              <div className="card-field">
                <label className="card-label">Agent ID</label>
                <div className="card-value">
                  <code className="agent-id-display">{agentId}</code>
                </div>
              </div>

              {/* Technical Details Field */}
              <div className="card-field">
                <label className="card-label">Technical Details</label>
                <div className="card-value">
                  <div className="tech-detail">
                    <strong>Protocol:</strong> HTTP Message Signatures
                  </div>
                  <div className="tech-detail">
                    <strong>Key Type:</strong> Ed25519
                  </div>
                </div>
              </div>

              {/* JWK Public Key Field (Collapsible) */}
              <div className="card-field">
                <label className="card-label">
                  <button 
                    onClick={() => setShowJWKDetails(!showJWKDetails)}
                    className="jwk-toggle-button"
                    type="button"
                  >
                    JWK Public Key {showJWKDetails ? '▼' : '▶'}
                  </button>
                </label>
                {showJWKDetails && (
                  <div className="card-value">
                    <div className="jwk-preview">
                      <pre className="jwk-json">
                        {JSON.stringify({
                          kty: "OKP",
                          crv: "Ed25519", 
                          kid: agentId,
                          x: "(generated during registration)"
                        }, null, 2)}
                      </pre>
                      <div className="jwk-note">
                        <em>Note: Actual public key will be generated during registration</em>
                      </div>
                    </div>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      ) : view === 'table' ? (
        <AgentTable 
          agents={agents} 
          onRegisterMore={handleRegisterMore}
          onOpenModal={handleOpenModal}
          openTooltip={openTooltip}
          onTooltipToggle={handleTooltipToggle}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          agentsPerPage={agentsPerPage}
        />
      ) : view === 'about' ? (
        <div className="main-content">
          <div className="form-panel">
            <h2 className="form-title">nekuda Agent Registry</h2>
            
            <div style={{fontFamily: "'Rubik', sans-serif", fontSize: '16px', lineHeight: '1.6', color: 'rgba(0,0,0,0.8)', maxWidth: '100%', width: '100%'}}>
              
              <div style={{marginBottom: '30px'}}>
                <h3 style={{color: '#333', fontSize: '20px', marginBottom: '15px', fontWeight: '600'}}>Why do we need a registry?</h3>
                <p style={{margin: '0 0 20px 0'}}>
                  AI agents are starting to use the web like humans—browsing, scraping, shopping, and acting on our behalf. But for websites, these agents still look like bots. That means they're blocked by bot detectors and fraud systems.
                </p>
                <p style={{margin: '0 0 20px 0'}}>
                  The problem? Many of these agents are good actors. They bring real value to websites—whether they're helping users shop or fetching data from a publisher. A new standard backed by Cloudflare (based on <a href="https://datatracker.ietf.org/doc/html/rfc9421" target="_blank" rel="noopener noreferrer" style={{color: '#007bff', textDecoration: 'none'}}>RFC 9421</a>) is here to fix that: it lets bots and agents identify themselves using digital signatures.
                </p>
                <p style={{margin: '0 0 20px 0'}}>
                  You can read more about the standard <a href="https://blog.cloudflare.com/verified-bots-with-cryptography/" target="_blank" rel="noopener noreferrer" style={{color: '#007bff', textDecoration: 'none'}}>here</a>, and access the docs and tools <a href="https://developers.cloudflare.com/bots/concepts/bot/verified-bots/web-bot-auth/" target="_blank" rel="noopener noreferrer" style={{color: '#007bff', textDecoration: 'none'}}>here</a>.
                </p>
              </div>

              <div style={{
                borderTop: '1px solid #e0e0e0',
                borderBottom: '1px solid #e0e0e0',
                padding: '30px 0',
                margin: '30px 0'
              }}>
                <h3 style={{color: '#333', fontSize: '20px', marginBottom: '15px', fontWeight: '600'}}>How it works</h3>
                <ol style={{margin: '0 0 20px 20px', paddingLeft: '0'}}>
                  <li style={{marginBottom: '8px'}}>You generate a cryptographic identity for your agent (a key pair).</li>
                  <li style={{marginBottom: '8px'}}>The agent signs each request using a library like web-bot-auth.</li>
                  <li style={{marginBottom: '8px'}}>The website validates the signature against the agent's public key—hosted in a public registry like the Nekuda Registry.</li>
                </ol>
                <p style={{margin: '0 0 20px 0'}}>
                  That's it. If the signature checks out, the request gets treated like a known, verified actor.
                </p>
              </div>

              <div style={{
                borderBottom: '1px solid #e0e0e0',
                paddingBottom: '30px',
                marginBottom: '30px'
              }}>
                <h3 style={{color: '#333', fontSize: '20px', marginBottom: '15px', fontWeight: '600'}}>Why use the Nekuda Registry?</h3>
                <p style={{margin: '0 0 15px 0'}}>Because we make it dead simple:</p>
                <ul style={{margin: '0 0 20px 20px', paddingLeft: '0'}}>
                  <li style={{marginBottom: '8px'}}>Generate keys instantly</li>
                  <li style={{marginBottom: '8px'}}>Host your public key in a compliant directory</li>
                  <li style={{marginBottom: '8px'}}>Register in seconds</li>
                </ul>
                <p style={{margin: '0 0 20px 0'}}>
                  We care about enabling agents that contribute to the internet economy without hurting store owners or publishers. Our goal is to help good bots be seen for what they are—useful, safe, and verifiable.
                </p>
              </div>


              <div style={{marginBottom: '30px'}}>
                <h3 style={{color: '#333', fontSize: '20px', marginBottom: '15px', fontWeight: '600'}}>Open Source & Community</h3>
                <p style={{margin: '0 0 20px 0'}}>
                  The nekuda Registry is committed to open standards and community collaboration. Our implementation is designed to be interoperable with other Web Bot Authentication systems and follows all relevant IETF specifications. We welcome feedback, contributions, and participation in the development of these emerging standards. Email us founders@nekuda.ai
                </p>
              </div>

            </div>
            
            <button className="submit-button" onClick={() => setView('form')}>Back to Registration</button>
          </div>
        </div>
      ) : view === 'sdk' ? (
        <div className="main-content">
          <div className="form-panel">
            <h2 className="form-title">Resources & Documentation</h2>
            
            {/* SDK Tabs */}
            <div style={{
              display: 'flex',
              borderBottom: '2px solid #e9ecef',
              marginBottom: '30px',
              gap: '20px'
            }}>
              <button
                onClick={() => setActiveSDKTab('agent-signing')}
                style={{
                  fontFamily: "'VT323', monospace",
                  fontSize: '18px',
                  padding: '10px 20px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  borderBottom: activeSDKTab === 'agent-signing' ? '3px solid #007bff' : '3px solid transparent',
                  color: activeSDKTab === 'agent-signing' ? '#007bff' : '#666',
                  fontWeight: activeSDKTab === 'agent-signing' ? 'bold' : 'normal',
                  textTransform: 'uppercase'
                }}
              >
                Agent Signing
              </button>
              <button
                onClick={() => setActiveSDKTab('request-verification')}
                style={{
                  fontFamily: "'VT323', monospace",
                  fontSize: '18px',
                  padding: '10px 20px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  borderBottom: activeSDKTab === 'request-verification' ? '3px solid #007bff' : '3px solid transparent',
                  color: activeSDKTab === 'request-verification' ? '#007bff' : '#666',
                  fontWeight: activeSDKTab === 'request-verification' ? 'bold' : 'normal',
                  textTransform: 'uppercase'
                }}
              >
                Request Verification
              </button>
              <button
                onClick={() => setActiveSDKTab('tools-extensions')}
                style={{
                  fontFamily: "'VT323', monospace",
                  fontSize: '18px',
                  padding: '10px 20px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  borderBottom: activeSDKTab === 'tools-extensions' ? '3px solid #007bff' : '3px solid transparent',
                  color: activeSDKTab === 'tools-extensions' ? '#007bff' : '#666',
                  fontWeight: activeSDKTab === 'tools-extensions' ? 'bold' : 'normal',
                  textTransform: 'uppercase'
                }}
              >
                Tools & Extensions
              </button>
              <button
                onClick={() => setActiveSDKTab('ietf-resources')}
                style={{
                  fontFamily: "'VT323', monospace",
                  fontSize: '18px',
                  padding: '10px 20px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  borderBottom: activeSDKTab === 'ietf-resources' ? '3px solid #007bff' : '3px solid transparent',
                  color: activeSDKTab === 'ietf-resources' ? '#007bff' : '#666',
                  fontWeight: activeSDKTab === 'ietf-resources' ? 'bold' : 'normal',
                  textTransform: 'uppercase'
                }}
              >
                IETF Resources
              </button>
            </div>

            <div style={{fontFamily: "'Rubik', sans-serif", fontSize: '16px', lineHeight: '1.6', color: 'rgba(0,0,0,0.8)', maxWidth: '100%', width: '100%'}}>
              
              {/* Agent Signing Tab */}
              {activeSDKTab === 'agent-signing' && (
                <div style={{ padding: '20px 0' }}>
                  <h3 style={{ color: '#333', fontSize: '24px', marginBottom: '20px', fontWeight: '600' }}>Agent Signing Implementation</h3>
                  
                  <div style={{ marginBottom: '30px' }}>
                    <h4 style={{ color: '#007bff', fontSize: '18px', marginBottom: '15px' }}>📦 Installation</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                      <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                        <h5 style={{ margin: '0 0 10px 0', color: '#495057' }}>Node.js / TypeScript</h5>
                        <div style={{ backgroundColor: '#fff3cd', padding: '10px', borderRadius: '4px', marginBottom: '10px', border: '1px solid #ffeaa7' }}>
                          <p style={{ margin: '0', fontSize: '12px', color: '#856404' }}>
                            <strong>Note:</strong> Official Cloudflare packages are in development. Use community implementations or follow Cloudflare documentation for manual implementation.
                          </p>
                        </div>
                        <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#6c757d' }}>
                          <a href="https://github.com/cloudflare/web-bot-auth" target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>
                            View on GitHub →
                          </a>
                        </p>
                      </div>
                      <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                        <h5 style={{ margin: '0 0 10px 0', color: '#495057' }}>Rust</h5>
                        <code style={{ backgroundColor: '#e9ecef', padding: '8px 12px', borderRadius: '4px', display: 'block', fontFamily: 'monospace' }}>
                          cargo add web-bot-auth
                        </code>
                        <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#6c757d' }}>
                          <a href="https://crates.io/crates/web-bot-auth" target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>
                            View on Crates.io →
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div style={{ marginBottom: '30px' }}>
                    <h4 style={{ color: '#007bff', fontSize: '18px', marginBottom: '15px' }}>🚀 Quick Start</h4>
                    <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                      <pre style={{ margin: 0, fontSize: '14px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflow: 'auto' }}>
{`// Node.js / TypeScript - Manual Implementation
// Follow Cloudflare's Web Bot Auth documentation for full implementation

import crypto from 'crypto';

// Generate HTTP Message Signatures
function generateSignature(request, privateKey, keyId) {
  // Implementation based on HTTP Message Signatures spec
  // See: https://developers.cloudflare.com/bots/concepts/bot/verified-bots/web-bot-auth/
  
  const components = ['@authority', 'signature-agent'];
  const created = Math.floor(Date.now() / 1000);
  const expires = created + 300; // 5 minutes
  
  // Construct signature base and sign
  // ... (implementation details in Cloudflare docs)
  
  return {
    'Signature': \`sig1=:\${signature}:\`,
    'Signature-Input': \`sig1=("\${components.join('" "')}");created=\${created};expires=\${expires};keyid="\${keyId}";alg="ed25519";tag="web-bot-auth"\`,
    'Signature-Agent': '"https://nekuda-agent-registry.onrender.com/.well-known/http-message-signatures-directory"'
  };
}`}
                      </pre>
                    </div>
                  </div>

                  <div style={{ marginBottom: '30px' }}>
                    <h4 style={{ color: '#007bff', fontSize: '18px', marginBottom: '15px' }}>🔧 Environment Setup</h4>
                    <div style={{ backgroundColor: '#fff3cd', padding: '15px', borderRadius: '8px', border: '1px solid #ffeaa7', marginBottom: '15px' }}>
                      <p style={{ margin: '0', fontSize: '14px', color: '#856404' }}>
                        <strong>⚠️ Security Note:</strong> Store your private key securely using environment variables or a secrets manager.
                      </p>
                    </div>
                    <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                      <pre style={{ margin: 0, fontSize: '14px', fontFamily: 'monospace' }}>
{`# Environment variables (.env file)
WEBBOT_PRIVATE_KEY=-----BEGIN PRIVATE KEY-----
...your private key...
-----END PRIVATE KEY-----

WEBBOT_AGENT_ID=AG_your_agent_id
WEBBOT_REGISTRY_URL=https://nekuda-agent-registry.onrender.com/.well-known/http-message-signatures-directory`}
                      </pre>
                    </div>
                  </div>

                  <div>
                    <h4 style={{ color: '#007bff', fontSize: '18px', marginBottom: '15px' }}>📚 Resources</h4>
                    <ul style={{ paddingLeft: '20px', lineHeight: '1.6' }}>
                      <li><a href="https://developers.cloudflare.com/bots/concepts/bot/verified-bots/web-bot-auth/" target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>Official Documentation</a></li>
                      <li><a href="https://datatracker.ietf.org/doc/draft-ietf-httpbis-message-signatures/" target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>HTTP Message Signatures Specification</a></li>
                      <li><a href="https://crawltest.com/cdn-cgi/web-bot-auth" target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>Testing Endpoint</a></li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Request Verification Tab */}
              {activeSDKTab === 'request-verification' && (
                <div style={{ padding: '20px 0' }}>
                  <h3 style={{ color: '#333', fontSize: '24px', marginBottom: '20px', fontWeight: '600' }}>Request Verification Implementation</h3>
                  
                  <div style={{ marginBottom: '30px' }}>
                    <h4 style={{ color: '#007bff', fontSize: '18px', marginBottom: '15px' }}>🧪 Test Your Integration</h4>
                    <div style={{ padding: '15px', backgroundColor: '#e7f3ff', borderRadius: '8px', border: '1px solid #b6d7ff', marginBottom: '20px' }}>
                      <h5 style={{ margin: '0 0 10px 0', color: '#0d47a1' }}>Cloudflare Test Endpoint</h5>
                      <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#1565c0' }}>
                        Use Cloudflare's official testing endpoint to validate your signatures:
                      </p>
                      <code style={{ backgroundColor: '#ffffff', padding: '8px 12px', borderRadius: '4px', display: 'block', fontFamily: 'monospace', border: '1px solid #b6d7ff', marginBottom: '10px' }}>
                        POST https://crawltest.com/cdn-cgi/web-bot-auth
                      </code>
                      <p style={{ margin: '0', fontSize: '12px', color: '#1565c0' }}>
                        <strong>Response Codes:</strong> 200 (Success), 401 (Invalid signature), 403 (Agent not found)
                      </p>
                    </div>
                  </div>

                  <div style={{ marginBottom: '30px' }}>
                    <h4 style={{ color: '#007bff', fontSize: '18px', marginBottom: '15px' }}>🛡️ Server-Side Verification</h4>
                    <p style={{ marginBottom: '20px', color: '#495057' }}>
                      Verify incoming signed requests from agents using HTTP Message Signatures.
                    </p>
                    <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                      <pre style={{ margin: 0, fontSize: '14px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflow: 'auto' }}>
{`// Express.js middleware example - Manual Implementation
import crypto from 'crypto';
import fetch from 'node-fetch';

// Verification middleware
app.use('/api/*', async (req, res, next) => {
  try {
    // Extract signature headers
    const signature = req.headers['signature'];
    const signatureInput = req.headers['signature-input'];
    const signatureAgent = req.headers['signature-agent'];
    
    if (!signature || !signatureInput || !signatureAgent) {
      return res.status(401).json({ error: 'Missing signature headers' });
    }
    
    // Fetch agent's public key from registry
    const agentRegistry = signatureAgent.replace(/"/g, '');
    const registryResponse = await fetch(agentRegistry);
    const agentData = await registryResponse.json();
    
    // Verify signature using HTTP Message Signatures spec
    const isValid = verifyHttpSignature(req, agentData.jwk, signature, signatureInput);
    
    if (isValid) {
      req.agent = agentData;
      console.log(\`Verified request from agent: \${agentData.agentId}\`);
      next();
    } else {
      res.status(401).json({ error: 'Invalid signature' });
    }
  } catch (error) {
    console.error('Verification failed:', error);
    res.status(500).json({ error: 'Verification error' });
  }
});`}
                      </pre>
                    </div>
                  </div>

                  <div style={{ marginBottom: '30px' }}>
                    <h4 style={{ color: '#007bff', fontSize: '18px', marginBottom: '15px' }}>🔍 Manual Verification</h4>
                    <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                      <pre style={{ margin: 0, fontSize: '14px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflow: 'auto' }}>
{`// Check agent registration
curl "https://nekuda-agent-registry.onrender.com/.well-known/http-message-signatures-directory/AG_your_agent_id"

// Expected response: JWK public key
{
  "kty": "OKP",
  "crv": "Ed25519", 
  "kid": "AG_your_agent_id",
  "x": "base64url_encoded_public_key"
}`}
                      </pre>
                    </div>
                  </div>


                  <div>
                    <h4 style={{ color: '#007bff', fontSize: '18px', marginBottom: '15px' }}>📋 Verification Checklist</h4>
                    <ul style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
                      <li>✅ Agent ID matches registered key in directory</li>
                      <li>✅ Signature headers are properly formatted</li>
                      <li>✅ Registry URL is accessible from your server</li>
                      <li>✅ HTTP Message Signatures follow IETF draft specification</li>
                      <li>✅ Test with Cloudflare's validation endpoint</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Tools & Extensions Tab */}
              {activeSDKTab === 'tools-extensions' && (
                <div style={{ padding: '20px 0' }}>
                  <h3 style={{ color: '#333', fontSize: '24px', marginBottom: '20px', fontWeight: '600' }}>CLI Tools & Extensions</h3>
                  
                  <div style={{ marginBottom: '30px' }}>
                    <h4 style={{ color: '#007bff', fontSize: '18px', marginBottom: '15px' }}>🛠️ Command Line Interface</h4>
                    <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', border: '1px solid #dee2e6', marginBottom: '15px' }}>
                      <h5 style={{ margin: '0 0 15px 0', color: '#495057' }}>HTTP Signature Directory CLI</h5>
                      <p style={{ margin: '0 0 15px 0', color: '#6c757d', fontSize: '14px' }}>
                        Official CLI tool for signing requests and verifying signatures against key directories.
                      </p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                        <div>
                          <strong style={{ color: '#495057' }}>Documentation:</strong>
                          <br />
                          <a href="https://developers.cloudflare.com/bots/concepts/bot/verified-bots/web-bot-auth/" target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', fontSize: '14px' }}>
                            Official Cloudflare Guide
                          </a>
                        </div>
                        <div>
                          <strong style={{ color: '#495057' }}>Implementation:</strong>
                          <br />
                          <code style={{ fontSize: '12px', color: '#6c757d' }}>
                            Follow manual implementation guide
                          </code>
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                      <pre style={{ margin: 0, fontSize: '14px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflow: 'auto' }}>
{`# Manual Implementation Steps

# 1. Generate Ed25519 key pair
openssl genpkey -algorithm ed25519 -out private-key.pem
openssl pkey -in private-key.pem -pubout -out public-key.pem

# 2. Convert public key to JWK format
# Use tools like jwker or implement conversion

# 3. Register your agent with the key registry
curl -X POST https://nekuda-agent-registry.onrender.com/v1/agents \\
  -H "Content-Type: application/json" \\
  -d '{"agentId":"AG_your_id","technical":{"jwk":{...}}}'

# 4. Implement HTTP Message Signatures in your application
# Follow the specification at:
# https://datatracker.ietf.org/doc/draft-ietf-httpbis-message-signatures/`}
                      </pre>
                    </div>
                  </div>


                  <div style={{ marginBottom: '30px' }}>
                    <h4 style={{ color: '#007bff', fontSize: '18px', marginBottom: '15px' }}>🧪 Testing & Debugging</h4>
                    <div style={{ backgroundColor: '#fff3cd', padding: '15px', borderRadius: '8px', border: '1px solid #ffeaa7', marginBottom: '15px' }}>
                      <p style={{ margin: '0', fontSize: '14px', color: '#856404' }}>
                        <strong>💡 Pro Tip:</strong> Use Cloudflare's test endpoint to validate your implementation before production deployment.
                      </p>
                    </div>
                    <ul style={{ paddingLeft: '20px', lineHeight: '1.6' }}>
                      <li><strong>Test Endpoint:</strong> <a href="https://crawltest.com/cdn-cgi/web-bot-auth" target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>crawltest.com/cdn-cgi/web-bot-auth</a></li>
                      <li><strong>Registry Check:</strong> <code style={{ backgroundColor: '#e9ecef', padding: '2px 6px', borderRadius: '3px', fontFamily: 'monospace', fontSize: '12px' }}>curl "registry-url/AG_your_id"</code></li>
                      <li><strong>Signature Validation:</strong> Verify headers follow IETF draft specification</li>
                      <li><strong>Key Format:</strong> Ensure JWK format compliance with RFC 7517</li>
                    </ul>
                  </div>

                  <div>
                    <h4 style={{ color: '#007bff', fontSize: '18px', marginBottom: '15px' }}>📚 Additional Resources</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                      <div style={{ padding: '15px', backgroundColor: '#e7f3ff', borderRadius: '8px', border: '1px solid #b6d7ff' }}>
                        <h5 style={{ margin: '0 0 10px 0', color: '#0d47a1' }}>Standards</h5>
                        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#1565c0' }}>
                          <li><a href="https://datatracker.ietf.org/doc/draft-ietf-httpbis-message-signatures/" target="_blank" rel="noopener noreferrer" style={{ color: '#0d47a1' }}>HTTP Message Signatures</a></li>
                          <li><a href="https://datatracker.ietf.org/doc/html/rfc7517" target="_blank" rel="noopener noreferrer" style={{ color: '#0d47a1' }}>JSON Web Keys (JWK)</a></li>
                          <li><a href="https://datatracker.ietf.org/doc/html/rfc8037" target="_blank" rel="noopener noreferrer" style={{ color: '#0d47a1' }}>Ed25519 for JWS</a></li>
                        </ul>
                      </div>
                      <div style={{ padding: '15px', backgroundColor: '#f3e5f5', borderRadius: '8px', border: '1px solid #ce93d8' }}>
                        <h5 style={{ margin: '0 0 10px 0', color: '#6a1b9a' }}>Community</h5>
                        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#7b1fa2' }}>
                          <li><a href="https://developers.cloudflare.com/bots/" target="_blank" rel="noopener noreferrer" style={{ color: '#6a1b9a' }}>Cloudflare Bot Management</a></li>
                          <li><a href="https://github.com/cloudflare" target="_blank" rel="noopener noreferrer" style={{ color: '#6a1b9a' }}>Cloudflare GitHub</a></li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

                             {/* IETF Resources Tab */}
               {activeSDKTab === 'ietf-resources' && (
                 <div style={{ padding: '20px 0' }}>
                   <h3 style={{ color: '#333', fontSize: '24px', marginBottom: '20px', fontWeight: '600' }}>IETF Resources</h3>
                   
                   <div style={{ marginBottom: '30px' }}>
                     <h4 style={{ color: '#007bff', fontSize: '18px', marginBottom: '15px' }}>🔗 Web Bot Auth Specifications</h4>
                     <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', border: '1px solid #dee2e6', marginBottom: '20px' }}>
                       <h5 style={{ margin: '0 0 15px 0', color: '#495057' }}>Directory Draft</h5>
                       <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#6c757d' }}>
                         Defines how bots can share their public keys through a well-known directory
                       </p>
                       <a href="https://datatracker.ietf.org/doc/html/draft-meunier-http-message-signatures-directory" target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', fontWeight: 'bold' }}>
                         draft-meunier-http-message-signatures-directory →
                       </a>
                     </div>
                     
                     <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                       <h5 style={{ margin: '0 0 15px 0', color: '#495057' }}>Protocol Draft</h5>
                       <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#6c757d' }}>
                         Defines how these keys should be used to attach bot identity to HTTP requests
                       </p>
                       <a href="https://datatracker.ietf.org/doc/html/draft-meunier-web-bot-auth-architecture" target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', fontWeight: 'bold' }}>
                         draft-meunier-web-bot-auth-architecture →
                       </a>
                     </div>
                   </div>

                   <div style={{ marginBottom: '30px' }}>
                     <h4 style={{ color: '#007bff', fontSize: '18px', marginBottom: '15px' }}>📚 Supporting Standards</h4>
                     <ul style={{ paddingLeft: '20px', lineHeight: '1.6' }}>
                       <li><a href="https://datatracker.ietf.org/doc/draft-ietf-httpbis-message-signatures/" target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>HTTP Message Signatures</a> - Core signing mechanism</li>
                       <li><a href="https://datatracker.ietf.org/doc/html/rfc7517" target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>JSON Web Keys (JWK)</a> - Key format specification</li>
                       <li><a href="https://datatracker.ietf.org/doc/html/rfc8037" target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>Ed25519 for JWS</a> - Cryptographic algorithm</li>
                     </ul>
                   </div>

                   <div style={{ marginBottom: '30px' }}>
                     <h4 style={{ color: '#007bff', fontSize: '18px', marginBottom: '15px' }}>🔍 Understanding the Standards</h4>
                     <div style={{ backgroundColor: '#e7f3ff', padding: '15px', borderRadius: '8px', border: '1px solid #b6d7ff' }}>
                       <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#1565c0' }}>
                         <strong>How it works:</strong> The directory draft allows bots to publish their public keys at a standard location, 
                         while the protocol draft defines how to cryptographically sign HTTP requests using these keys.
                       </p>
                       <p style={{ margin: '0', fontSize: '14px', color: '#1565c0' }}>
                         This creates a decentralized system where any bot can prove its identity without relying on centralized authorities.
                       </p>
                     </div>
                   </div>
                 </div>
               )}

            </div>
            
            <button className="submit-button" onClick={() => setView('form')}>Back to Registration</button>
          </div>
        </div>
      ) : (
        <div className="main-content">
          <div className="form-panel">
            <h2 className="form-title">Verify Your Agent</h2>
            <p style={{fontFamily: "'Rubik', sans-serif", fontSize: '16px', lineHeight: '1.6', margin: '20px 0', color: 'rgba(0,0,0,0.8)'}}>
              Submit your agent for official verification. Once verified, your agent will receive a "verified" badge in the registry.
            </p>
            
            <div className="form-group">
              <label className="form-label">Agent ID *</label>
              <input
                type="text"
                name="agentId"
                className="form-input"
                value={verificationData.agentId}
                onChange={handleVerificationInputChange}
                placeholder="AG_xxxxxxxx"
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Contact Email *</label>
              <input
                type="email"
                name="email"
                className="form-input"
                value={verificationData.email}
                onChange={handleVerificationInputChange}
                placeholder="you@example.com"
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Additional Information</label>
              <textarea
                name="info"
                className="form-textarea"
                value={verificationData.info}
                onChange={handleVerificationInputChange}
                placeholder="Provide any extra details for the verification team."
                disabled={isSubmitting}
              />
            </div>
            
            <button className="submit-button" onClick={handleVerificationSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Request Verification'}
            </button>
            <button className="submit-button" onClick={() => setView('form')} style={{marginTop: '10px', backgroundColor: '#6c757d'}} disabled={isSubmitting}>
              Back to Registration
            </button>
            {showVerificationSuccess && <div className="success-popup">Verification Request Sent!</div>}
          </div>
        </div>
      )}


      {/* Agent Modal */}
      {isModalOpen && selectedAgent && (
        <div 
          onClick={handleCloseModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(5px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '800px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
          >
            {/* Close Button */}
            <button 
              onClick={handleCloseModal}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'none',
                border: 'none',
                color: '#007bff',
                fontSize: '28px',
                cursor: 'pointer',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0, 123, 255, 0.1)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              ×
            </button>

            <h2 style={{
              fontFamily: "'VT323', monospace",
              fontSize: '32px',
              margin: '0 40px 25px 0',
              color: '#333'
            }}>
              Agent Card
            </h2>
            
            <div style={{fontFamily: "'Rubik', sans-serif", fontSize: '16px', lineHeight: '1.6'}}>

              {/* Agent Details */}
              <div style={{display: 'grid', gap: '20px'}}>
                
                {/* Agent ID */}
                <div style={{
                  padding: '20px',
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef'
                }}>
                  <h3 style={{fontFamily: "'VT323', monospace", fontSize: '20px', marginBottom: '15px', color: '#333'}}>Agent ID</h3>
                  <div style={{display: 'grid', gap: '12px'}}>
                    <div><code style={{backgroundColor: '#f1f3f4', padding: '8px 12px', borderRadius: '4px', fontSize: '16px'}}>{selectedAgent.agentId}</code></div>
                  </div>
                </div>

                {/* Public Key */}
                <div style={{
                  padding: '20px',
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef'
                }}>
                  <h3 style={{fontFamily: "'VT323', monospace", fontSize: '20px', marginBottom: '15px', color: '#333'}}>Public Key</h3>
                  <div style={{display: 'grid', gap: '12px'}}>
                    {selectedAgent.technical?.jwk && (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                          <strong>JWK (JSON Web Key):</strong>
                          <div style={{ position: 'relative', display: 'inline-block' }}>
                            <button
                              onClick={() => handleCopyJWK(selectedAgent.agentId, selectedAgent.technical.jwk)}
                              onMouseEnter={() => setModalHoveredButtons(prev => ({ ...prev, [`${selectedAgent.agentId}_jwk`]: true }))}
                              onMouseLeave={() => setModalHoveredButtons(prev => ({ ...prev, [`${selectedAgent.agentId}_jwk`]: false }))}
                              style={{
                                background: modalCopiedStates[`${selectedAgent.agentId}_jwk`] ? '#28a745'
                                           : modalHoveredButtons[`${selectedAgent.agentId}_jwk`] ? '#007bff' : 'none',
                                border: `1px solid ${modalCopiedStates[`${selectedAgent.agentId}_jwk`] ? '#28a745'
                                           : modalHoveredButtons[`${selectedAgent.agentId}_jwk`] ? '#007bff' : '#ccc'}`,
                                borderRadius: '4px',
                                padding: '4px 8px',
                                cursor: 'pointer',
                                fontSize: '10px',
                                color: modalCopiedStates[`${selectedAgent.agentId}_jwk`] || modalHoveredButtons[`${selectedAgent.agentId}_jwk`] ? 'white' : '#666',
                                minWidth: '40px',
                                whiteSpace: 'nowrap',
                                transition: 'all 0.2s ease-in-out',
                                position: 'relative'
                              }}
                              className="copy-button"
                            >
                              {modalCopiedStates[`${selectedAgent.agentId}_jwk`] ? '✓' : 'Copy'}
                            </button>

                            {(modalHoveredButtons[`${selectedAgent.agentId}_jwk`] || modalCopiedStates[`${selectedAgent.agentId}_jwk`]) && (
                              <div
                                style={{
                                  position: 'absolute',
                                  bottom: '100%',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  backgroundColor: '#333',
                                  color: 'white',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  whiteSpace: 'nowrap',
                                  zIndex: '1000',
                                  marginBottom: '4px',
                                  pointerEvents: 'none',
                                  opacity: 1,
                                  visibility: 'visible',
                                  animation: 'fadeIn 0.2s ease-in-out'
                                }}
                                className="copy-tooltip"
                              >
                                {modalCopiedStates[`${selectedAgent.agentId}_jwk`] ? 'Copied!' : 'Copy JWK'}
                                <div style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  width: '0',
                                  height: '0',
                                  borderLeft: '4px solid transparent',
                                  borderRight: '4px solid transparent',
                                  borderTop: '4px solid #333'
                                }}></div>
                              </div>
                            )}
                          </div>
                        </div>
                        <pre style={{
                          backgroundColor: '#f1f3f4', 
                          padding: '12px', 
                          borderRadius: '4px', 
                          display: 'block',
                          marginTop: '5px',
                          fontSize: '12px',
                          overflow: 'auto',
                          fontFamily: "'Courier New', monospace"
                        }}>
                          {JSON.stringify(selectedAgent.technical.jwk, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>

                {/* Registry URL */}
                <div style={{
                  padding: '20px',
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  border: '1px solid #e9ecef'
                }}>
                  <h3 style={{fontFamily: "'VT323', monospace", fontSize: '20px', marginBottom: '15px', color: '#333'}}>Registry URL</h3>
                  <div style={{display: 'grid', gap: '12px'}}>
                    <div>
                      <a
                        href={`https://nekuda-agent-registry.onrender.com/.well-known/http-message-signatures-directory/${selectedAgent.agentId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#007bff',
                          textDecoration: 'underline',
                          fontSize: '14px',
                          fontFamily: 'monospace',
                          wordBreak: 'break-all'
                        }}
                      >
                        https://nekuda-agent-registry.onrender.com/.well-known/http-message-signatures-directory/{selectedAgent.agentId}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="error-message" style={{
          color: '#dc3545',
          backgroundColor: '#f8d7da',
          padding: '10px',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {/* Browser compatibility warning */}
      {!browserSupported && (
        <div className="browser-warning">
          ⚠️ Your browser does not support the required cryptographic operations.
          Please use a modern browser like Chrome, Firefox, Safari, or Edge.
        </div>
      )}

      {/* Success Modal */}
      {showKeyModal && (
        <KeyGenerationModal
          agentData={registeredAgent}
          keyMaterial={generatedKeys}
          onClose={() => {
            setShowKeyModal(false);
            setView('table');
          }}
        />
      )}


    </div>
  );
};

export default NekudaAgentRegistry; 